import React from 'react';
import { View, Text, ScrollView, StyleSheet, TouchableOpacity } from 'react-native';
import { DesktopPanelHeader } from '@/components/common/admin/desktop/panel/DesktopPanelHeader';
import { ADMIN_COLORS } from '@/constants/admin';
import { AvatarCircle } from '@/components';
import { SVG_COMPONENTS } from '@/assets/admin';
import { IConversation, IConversationAccount } from '@/types/admin/messages';
import { DesktopSidePanel } from '@/components/common/admin/desktop/panel/DesktopSidePanel';
import { ResidentDetailsSidePanel } from '@/components/admin/resident/desktop/ResidentDetailsSidePanel';

interface ConversationDetailsPanelProps {
  conversation: IConversation;
  onClose: () => void;
}

export const ConversationDetailsPanel: React.FC<ConversationDetailsPanelProps> = ({ conversation, onClose }) => {
  const participants: IConversationAccount[] = (conversation?.conversationAccounts || []).filter((acct: any) => acct.type === 'resident');
  const threadOwners: IConversationAccount[] = (conversation?.conversationAccounts || []).filter((acct: any) => acct.type === 'admin');

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Local state for resident drilldown side panel
  const [showResidentPanel, setShowResidentPanel] = React.useState(false);
  const [residentParams, setResidentParams] = React.useState<{ accountId: number | null; leaseId: number | null }>({ accountId: null, leaseId: null });

  const openResidentPanel = (participant: IConversationAccount) => {
    const assignee = participant.assigneeAccount;
    const leaseId = assignee?.leaseCustomers?.[0]?.lease?.id || null;
    const accountId = assignee?.id || null;
    if (accountId && leaseId) {
      setResidentParams({ accountId, leaseId });
      setShowResidentPanel(true);
    }
  };

  return (
    <View style={styles.container}>
      <DesktopPanelHeader title="Conversation Details" onClose={onClose} />

      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 16 }}>
        {/* Conversation info */}
        <View style={{ paddingHorizontal: 16, paddingTop: 16 }}>
          <View style={{ backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY, borderRadius: 12, padding: 16 }}>
            <Text style={{ fontSize: 16, fontWeight: '600', color: ADMIN_COLORS.TEXT_PRIMARY, marginBottom: 6 }} numberOfLines={2}>{conversation.subject}</Text>
            <Text style={{ fontSize: 14, color: ADMIN_COLORS.TEXT_SECONDARY }}>Created {formatDate(conversation.createdAt)}</Text>
          </View>
        </View>

        {/* Participants */}
        <View style={{ paddingHorizontal: 16, paddingTop: 20 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Text style={{ fontSize: 18, fontWeight: '600', color: ADMIN_COLORS.TEXT_PRIMARY }}>Participants</Text>
            <Text style={{ fontSize: 16, color: ADMIN_COLORS.TEXT_SECONDARY }}>{participants.length}</Text>
          </View>

          {participants.length === 0 ? (
            <View style={{ alignItems: 'center', paddingVertical: 24 }}>
              <SVG_COMPONENTS.GROUP width={32} height={32} color={ADMIN_COLORS.TEXT_SECONDARY} />
              <Text style={{ fontSize: 14, color: ADMIN_COLORS.TEXT_SECONDARY, marginTop: 8 }}>No participants</Text>
            </View>
          ) : (
            participants.map((participant) => {
              const nameString = participant.assigneeAccount.firstName && participant.assigneeAccount.lastName
                ? `${participant.assigneeAccount.firstName} ${participant.assigneeAccount.lastName}`
                : participant.assigneeAccount.email;
              const assigneeLC = participant.assigneeAccount.leaseCustomers?.[0];
              const leaseStatus = assigneeLC?.lease?.statusType;
              const hasLoggedIn = Boolean(participant.assigneeAccount.lastInteractionDate);
              return (
                <TouchableOpacity
                  key={participant.id}
                  activeOpacity={0.8}
                  onPress={() => openResidentPanel(participant)}
                  style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY, borderRadius: 12, padding: 12, marginBottom: 8 }}
                >
                  <AvatarCircle firstName={participant.assigneeAccount.firstName || ''} lastName={participant.assigneeAccount.lastName || ''} size={40} style={{ marginRight: 12 }} />
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 16, fontWeight: '600', color: ADMIN_COLORS.TEXT_PRIMARY, marginBottom: 2 }}>{nameString}</Text>
                    {leaseStatus ? (
                      <Text style={{ fontSize: 14, color: ADMIN_COLORS.TEXT_SECONDARY }}>{leaseStatus} resident</Text>
                    ) : (
                      !hasLoggedIn && <Text style={{ fontSize: 12, color: ADMIN_COLORS.TEXT_TERTIARY, fontStyle: 'italic' }}>Has not created a Resio account</Text>
                    )}
                  </View>
                </TouchableOpacity>
              );
            })
          )}
        </View>

        {/* Thread Owners */}
        <View style={{ paddingHorizontal: 16, paddingTop: 20 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Text style={{ fontSize: 18, fontWeight: '600', color: ADMIN_COLORS.TEXT_PRIMARY }}>Thread Owners</Text>
            <Text style={{ fontSize: 16, color: ADMIN_COLORS.TEXT_SECONDARY }}>{threadOwners.length}</Text>
          </View>

          {threadOwners.length === 0 ? (
            <View style={{ alignItems: 'center', paddingVertical: 24 }}>
              <SVG_COMPONENTS.ADMIN width={32} height={32} color={ADMIN_COLORS.TEXT_SECONDARY} />
              <Text style={{ fontSize: 14, color: ADMIN_COLORS.TEXT_SECONDARY, marginTop: 8 }}>No thread owners assigned</Text>
            </View>
          ) : (
            threadOwners.map((owner) => {
              const nameString = owner.assigneeAccount.firstName && owner.assigneeAccount.lastName
                ? `${owner.assigneeAccount.firstName} ${owner.assigneeAccount.lastName}`
                : owner.assigneeAccount.email;
              return (
                <View key={owner.id} style={{ backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY, borderRadius: 12, padding: 16, marginBottom: 8 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
                    <AvatarCircle firstName={owner.assigneeAccount.firstName || ''} lastName={owner.assigneeAccount.lastName || ''} size={40} style={{ marginRight: 12 }} />
                    <View style={{ flex: 1 }}>
                      <Text style={{ fontSize: 16, fontWeight: '600', color: ADMIN_COLORS.TEXT_PRIMARY, marginBottom: 2 }}>{nameString}</Text>
                      <Text style={{ fontSize: 14, color: ADMIN_COLORS.TEXT_SECONDARY }}>Admin</Text>
                    </View>
                  </View>
                  {owner.assignedAt && (
                    <View style={{ paddingLeft: 52 }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                        <SVG_COMPONENTS.CALENDAR width={16} height={16} color={ADMIN_COLORS.TEXT_SECONDARY} />
                        <Text style={{ fontSize: 14, fontWeight: '500', color: ADMIN_COLORS.TEXT_PRIMARY, marginLeft: 8 }}>{new Date(owner.assignedAt).toLocaleString()}</Text>
                      </View>
                      <Text style={{ fontSize: 12, color: ADMIN_COLORS.TEXT_TERTIARY, lineHeight: 16 }}>
                        {nameString} assigned to this thread by {owner.assignedBy?.firstName ? `${owner.assignedBy.firstName} ${owner.assignedBy.lastName}` : owner.assignedBy?.email || 'System'}
                      </Text>
                    </View>
                  )}
                </View>
              );
            })
          )}
        </View>
      </ScrollView>
      {/* Resident details side panel */}
      <ResidentDetailsSidePanel
        visible={showResidentPanel}
        onClose={() => setShowResidentPanel(false)}
        accountId={residentParams.accountId}
        leaseId={residentParams.leaseId}
        mode={'account'}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
});
